import * as React from "react";
import store from "../../../../store";
import { useToasts } from "react-toast-notifications";
import Loader from "../../bits/Loader/Loader";
import { Button } from "../../bits/Button/Button";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Input } from "../../bits/Input/Input";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import MoreIcon from "../../icons/More";
import styles from "./ManageMethodology.module.scss";
import Alert from "../../bits/Alert/Alert";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";

export default function ManageMethodology() {
    const { addToast } = useToasts();
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState<string[]>([]);
    const [filteredOptions, setFilteredOptions] = React.useState<string[]>([]);
    const [newOptionName, setNewOptionName] = React.useState("");
    const [showAddDialog, setShowAddDialog] = React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState<string | null>(null);

    const fetchOptions = async () => {
        try {
            const data = await store.getAllMethodologyOptions();
            const sorted = [...data].sort();
            setOptions(sorted);
            setFilteredOptions(sorted);
        } catch {
            addToast("Failed to load options", { appearance: "error" });
        }
    };

    React.useEffect(() => {
        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = newOptionName.trim();
        if (!trimmed) return;
        if (/[./]/.test(trimmed)) {
            addToast("Option name cannot contain '.' or '/'", {
                appearance: "error",
            });
            return;
        }
        if (options.map((o) => o.toLowerCase()).includes(trimmed.toLowerCase())) {
            addToast("This option already exists", { appearance: "error" });
            return;
        }
        setLoading(true);
        try {
            await store.addMethodologyOption(trimmed);
            await fetchOptions();
            setNewOptionName("");
            setShowAddDialog(false);
            addToast("New option added", { appearance: "success" });
        } catch {
            addToast("Error adding option", { appearance: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedOption) return;
        setLoading(true);
        try {
            await store.deleteMethodologyOption(selectedOption);
            await fetchOptions();
            setShowDeleteDialog(false);
            setSelectedOption(null);
            addToast("Option deleted", { appearance: "success" });
        } catch {
            addToast("Error deleting option", { appearance: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="v2-page">
            <Loader
                show={loading}
                loadingText="Please wait while we process your request. This may take a moment."
            />
            <AlertDialog
                open={showDeleteDialog && Boolean(selectedOption)}
                onClose={() => setShowDeleteDialog(false)}
                onSaveClick={handleDelete}
                isDeleting
                title="Delete Intake-Other Option"
                description={`Are you sure you want to delete "${selectedOption}"? This will remove it from all clinician profiles.`}
                saveText="Delete"
                onCancelClick={() => setShowDeleteDialog(false)}
            />
            <Dialog
                open={showAddDialog}
                title="New Intake-Other Option"
                onCancel={() => setShowAddDialog(false)}
                onClose={() => setShowAddDialog(false)}
                submitBtnFormValue="add-methodology-form"
                saveText="Add option"
            >
                <form
                    id="add-methodology-form"
                    className="v2-form"
                    onSubmit={handleAdd}
                >
                    <Input
                        isRequired
                        label="Name"
                        name="name"
                        placeholder="Enter option name"
                        value={newOptionName}
                        onChange={(e) => setNewOptionName(e.target.value)}
                    />
                </form>
            </Dialog>
            <div className={styles.container}>
                <div className={styles.header}>
                    <SearchInput
                        placeholder="Search options"
                        onChange={(e) => {
                            const q = e.target.value.toLowerCase();
                            if (q.length > 1) {
                                setFilteredOptions(
                                    options.filter((o) =>
                                        o.toLowerCase().includes(q)
                                    )
                                );
                            } else {
                                setFilteredOptions(options);
                            }
                        }}
                    />
                    <Button onClick={() => setShowAddDialog(true)}>
                        Add Option
                    </Button>
                </div>

                <Alert type="info">
                    <p>This page can be viewed by the intake team members only.</p>
                </Alert>

                {filteredOptions.length === 0 && (
                    <EmptyListState
                        emptyMessage={`No "Intake Other" options found`}
                        stateHelperText={`No "Intake Other" options will be shown here`}
                    />
                )}
                <div className={styles.optionsContainer}>
                    {filteredOptions.map((option, idx) => (
                        <div
                            key={`methodology-${idx}`}
                            className={styles.optionCard}
                        >
                            <span className={styles.optionName}>{option}</span>
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <MoreIcon />
                                </Dropdown.Trigger>
                                <Dropdown.Content align="end">
                                    <Dropdown.Item
                                        onClick={() => {
                                            setSelectedOption(option);
                                            setShowDeleteDialog(true);
                                        }}
                                    >
                                        Delete
                                    </Dropdown.Item>
                                </Dropdown.Content>
                            </Dropdown>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
